import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "@/lib/utils";
const TabsContext = React.createContext(undefined);
const useTabsContext = () => {
    const context = React.useContext(TabsContext);
    if (!context) {
        throw new Error("Tabs components must be used within a Tabs provider");
    }
    return context;
};
const Tabs = React.forwardRef(({ className, value, defaultValue, onValueChange, children, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue || "");
    const currentValue = value !== undefined ? value : internalValue;
    const handleValueChange = React.useCallback((newValue) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }
        onValueChange?.(newValue);
    }, [value, onValueChange]);
    return (_jsx(TabsContext.Provider, { value: { value: currentValue, onValueChange: handleValueChange }, children: _jsx("div", { ref: ref, className: cn("w-full", className), ...props, children: children }) }));
});
Tabs.displayName = "Tabs";
const TabsList = React.forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn("flex items-center justify-center rounded-md bg-muted p-1 text-muted-foreground", className), ...props })));
TabsList.displayName = "TabsList";
const TabsTrigger = React.forwardRef(({ className, value, active, children, onClick, ...props }, ref) => {
    // Try to use context, but allow standalone usage
    let isActive = active;
    let handleClick = onClick;
    try {
        const context = useTabsContext();
        if (value !== undefined) {
            isActive = context.value === value;
            handleClick = (e) => {
                context.onValueChange(value);
                onClick?.(e);
            };
        }
    }
    catch {
        // No context, use manual active prop
    }
    return (_jsx("button", { ref: ref, type: "button", role: "tab", "aria-selected": isActive, className: cn("inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50", isActive
            ? "bg-background text-foreground shadow-sm"
            : "hover:bg-background/50 hover:text-foreground", className), onClick: handleClick, ...props, children: children }));
});
TabsTrigger.displayName = "TabsTrigger";
const TabsContent = React.forwardRef(({ className, value, children, ...props }, ref) => {
    // If value is provided, use context-based visibility
    if (value !== undefined) {
        try {
            const { value: selectedValue } = useTabsContext();
            if (selectedValue !== value) {
                return null;
            }
        }
        catch {
            // No context, render content (manual mode uses className hidden)
        }
    }
    return (_jsx("div", { ref: ref, role: "tabpanel", className: cn("mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", className), ...props, children: children }));
});
TabsContent.displayName = "TabsContent";
// TabsRoot is an alias for backward compatibility (simple wrapper without state management)
const TabsRoot = React.forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, className: cn("w-full", className), ...props })));
TabsRoot.displayName = "TabsRoot";
export { Tabs, TabsRoot, TabsList, TabsTrigger, TabsContent };
//# sourceMappingURL=tabs.js.map